import { afterRender, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, Input, OnChanges, ViewChild } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';

export interface MatSelectChipItem {
  option: MatOption;
  label: string;
  disabled: boolean;
}

const HIDDEN_CLASS = 'app-select-chip--hidden';
const SHRINK_CLASS = 'app-select-chip--shrink';
const CHIP_GAP = 4;

@Component({
  selector: 'app-mat-select-chips',
  standalone: true,
  imports: [MatChipsModule, MatIconModule, MatTooltipModule],
  templateUrl: './mat-select-chips.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'aria-hidden': 'true', '[class.app-select-chips--single-row]': 'maxRows === 1' },
})
export class MatSelectChipsComponent implements OnChanges {
  @Input() items: MatSelectChipItem[] = [];
  @Input() disabled = false;
  @Input() maxRows = 1;

  @ViewChild('moreTooltip') private moreTooltip?: MatTooltip;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private needsMeasure = true;
  private lastWidth = 0;

  constructor() {
    afterRender(() => {
      if (!this.needsMeasure) return;
      this.needsMeasure = false;
      this.measure();
    });

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        const width = this.host.clientWidth;
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        this.measure();
      });
      resizeObserver.observe(this.host);
      inject(DestroyRef).onDestroy(() => resizeObserver.disconnect());
    }
  }

  ngOnChanges(): void {
    this.needsMeasure = true;
  }

  remove(item: MatSelectChipItem): void {
    if (this.disabled || item.disabled) return;
    item.option._getHostElement().click();
  }

  syncLabelTooltip(tooltip: MatTooltip, label: HTMLElement): void {
    tooltip.disabled = label.scrollWidth <= label.clientWidth;
  }

  private measure(): void {
    const chips = Array.from(this.host.querySelectorAll<HTMLElement>('.app-select-chip:not(.app-select-chip--more)'));
    const more = this.host.querySelector<HTMLElement>('.app-select-chip--more');
    if (!more) return;
    const row = more.parentElement as HTMLElement;

    chips.forEach((chip) => {
      chip.classList.remove(HIDDEN_CLASS, SHRINK_CLASS);
      chip.style.removeProperty('max-width');
    });
    more.classList.add(HIDDEN_CLASS);
    if (this.maxRows <= 0 || chips.length === 0) return;

    const moreLabel = more.querySelector<HTMLElement>('.app-select-chip__label');
    const setMoreCount = (count: number): void => {
      if (moreLabel) moreLabel.textContent = `+${count} more`;
    };
    let fit = chips.length;

    if (this.maxRows === 1) {
      const overflowing = (): boolean => {
        const visible = [...chips, more].filter((el) => !el.classList.contains(HIDDEN_CLASS));
        const last = visible[visible.length - 1];
        return !!last && last.getBoundingClientRect().right > row.getBoundingClientRect().right + 0.5;
      };
      if (overflowing()) {
        if (chips.length > 1) {
          more.classList.remove(HIDDEN_CLASS);
          do {
            fit--;
            chips[fit].classList.add(HIDDEN_CLASS);
            setMoreCount(chips.length - fit);
          } while (fit > 1 && overflowing());
        }
        if (overflowing()) chips[0].classList.add(SHRINK_CLASS);
        if (overflowing() && moreLabel) moreLabel.textContent = `+${chips.length - fit}`;
        if (overflowing()) {
          chips[0].classList.add(HIDDEN_CLASS);
          fit = 0;
          more.classList.remove(HIDDEN_CLASS);
          if (moreLabel) moreLabel.textContent = `${chips.length} selected`;
        }
      }
    } else {
      const firstTop = chips[0].offsetTop;
      const pitch = chips[0].offsetHeight + CHIP_GAP;
      const rowOf = (el: HTMLElement): number => Math.round((el.offsetTop - firstTop) / pitch);

      fit = chips.findIndex((chip) => rowOf(chip) >= this.maxRows);
      if (fit === -1) return;

      more.classList.remove(HIDDEN_CLASS);
      for (let i = fit; i < chips.length; i++) chips[i].classList.add(HIDDEN_CLASS);
      setMoreCount(chips.length - fit);
      while (fit > 1 && rowOf(more) >= this.maxRows) {
        fit--;
        chips[fit].classList.add(HIDDEN_CLASS);
        setMoreCount(chips.length - fit);
      }
      if (rowOf(more) >= this.maxRows) {
        const available = row.clientWidth - more.getBoundingClientRect().width - CHIP_GAP - 2;
        chips[0].style.maxWidth = `${Math.max(available, 0)}px`;
      }
    }

    if (this.moreTooltip) {
      this.moreTooltip.message = this.items
        .slice(fit)
        .map((item) => item.label)
        .join(', ');
    }
  }
}
